<template>
  <div class="bpmn-instance-page">
    <div class="bpmn-instance-page__header">
      <router-link to="/work-check-bpmn-active/instances" class="bpmn-instance-page__back">
        ← Все процессы
      </router-link>
      <div v-if="instance" class="bpmn-instance-page__title-row">
        <h2 class="bpmn-instance-page__title">
          Процесс #{{ instance.process_instance_id }} — Схема {{ instance.schema_id }}
        </h2>
        <span class="instance-badge" :class="'instance-badge--' + instance.status">
          {{ instance.status === 'active' ? 'Активен' : 'Завершён' }}
        </span>
      </div>
      <div v-if="instance" class="bpmn-instance-page__meta">
        <span>Группа работ: <b>{{ instance.work_set_check_group_id }}</b></span>
        <span>Этаж: <b>{{ instance.floor_map_id }}</b></span>
        <span>Тип пом.: <b>{{ instance.placement_type_id }}</b></span>
        <span>Запущен: <b>{{ formatDate(instance.started_at) }}</b></span>
        <span v-if="instance.current_node_labels?.length" class="bpmn-instance-page__current">
          📍 {{ instance.current_node_labels.join(' · ') }}
        </span>
      </div>
      <button class="bpmn-instance-page__refresh" @click="load">
        ↻ Обновить
      </button>
    </div>

    <div v-if="loading" class="bpmn-instance-page__state">Загрузка...</div>
    <div v-else-if="error" class="bpmn-instance-page__state bpmn-instance-page__state--error">{{ error }}</div>
    <template v-else-if="instance">
      <BpmnFlow
        :schema-id="instance.schema_id"
        :current-node-ids="instance.current_bpmn_node_ids || []"
        :completed-node-ids="instance.completed_bpmn_node_ids || []"
        :instance-nodes="instanceNodes"
        @fire-event="openFireModal"
      />
    </template>

    <!-- Модал ручного запуска события -->
    <BpmnFireEventModal
      v-if="fireModalNode"
      :instance-node="fireModalNode"
      :process-instance-id="parseInt(route.params.id)"
      :schema-edges="schemaEdges"
      @close="fireModalNode = null"
      @success="onFireSuccess"
    />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import BpmnFlow from './BpmnFlow.vue'
import BpmnFireEventModal from './BpmnFireEventModal.vue'
import { wcheckBpmnAPI } from './services/api'
import { useBpmnDicts } from './useBpmnDicts'

const route = useRoute()
const bpmnDicts = useBpmnDicts()

const instance = ref(null)
const instanceNodes = ref([])
const schemaEdges = ref([])
const loading = ref(false)
const error = ref(null)
const fireModalNode = ref(null)

async function load() {
  loading.value = true
  error.value = null
  try {
    const id = parseInt(route.params.id)
    const [instRes, nodesRes] = await Promise.all([
      wcheckBpmnAPI.getProcessInstances(),
      wcheckBpmnAPI.getInstanceNodes(id),
    ])
    const all = instRes?.data || []
    instance.value = all.find((i) => i.process_instance_id === id) || null
    instanceNodes.value = nodesRes?.data || []
    if (!instance.value) {
      error.value = `Процесс #${id} не найден`
      return
    }
    // Загружаем рёбра схемы для модала
    const edgesRes = await wcheckBpmnAPI.getEdges(instance.value.schema_id)
    schemaEdges.value = edgesRes?.data || []
  } catch (e) {
    error.value = e?.response?.data?.error || e?.message || 'Ошибка загрузки'
  } finally {
    loading.value = false
  }
}

function openFireModal(instanceNode) {
  fireModalNode.value = instanceNode
}

function onFireSuccess() {
  fireModalNode.value = null
  load()
}

function formatDate(dt) {
  if (!dt) return '—'
  return new Date(dt).toLocaleString('ru-RU')
}

onMounted(() => {
  bpmnDicts.load()
  load()
})
</script>

<style scoped>
.bpmn-instance-page {
  padding: 20px;
  max-width: 1600px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.bpmn-instance-page__header {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.bpmn-instance-page__back {
  font-size: 13px;
  color: #1976d2;
  text-decoration: none;
  width: fit-content;
}
.bpmn-instance-page__back:hover {
  text-decoration: underline;
}

.bpmn-instance-page__title-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.bpmn-instance-page__title {
  font-size: 20px;
  font-weight: 700;
  color: #212121;
  margin: 0;
}

.instance-badge {
  padding: 3px 10px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
}
.instance-badge--active {
  background: #c8e6c9;
  color: #2e7d32;
}
.instance-badge--completed {
  background: #e0e0e0;
  color: #616161;
}

.bpmn-instance-page__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  font-size: 13px;
  color: #546e7a;
}

.bpmn-instance-page__current {
  color: #f57c00;
  font-weight: 600;
}

.bpmn-instance-page__refresh {
  padding: 6px 14px;
  background: #e3f2fd;
  border: 1px solid #90caf9;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  color: #1565c0;
  width: fit-content;
}
.bpmn-instance-page__refresh:hover {
  background: #bbdefb;
}

.bpmn-instance-page__state {
  padding: 40px;
  text-align: center;
  color: #757575;
  font-size: 14px;
}
.bpmn-instance-page__state--error {
  color: #c62828;
}
</style>
